/**
 * A tiny external store tracking which mindmaps have a refresh in flight (V3
 * P7). `refreshMindmapFromNotion` marks/clears its mapId around the run;
 * RenderMindmapNode's root hover overlay subscribes and swaps the refresh icon
 * for a spinner, so a click has visible feedback — the owner clicked refresh and
 * could not tell anything had happened.
 *
 * Keyed by mapId, not element id: a refresh re-walks the WHOLE map, so it is the
 * map that is busy, not the pill that was clicked (contrast `mindmapExpandBusy`,
 * where each chip fetches only its own subtree). Kept in its own module so the
 * DOM renderer can subscribe without importing the refresh pipeline.
 */

const inFlight = new Set<string>();
const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

/** Mark a map's refresh as in flight and notify subscribers. */
export const markRefreshInFlight = (mapId: string): void => {
  inFlight.add(mapId);
  notify();
};

/** Clear a map's in-flight mark and notify subscribers. */
export const clearRefreshInFlight = (mapId: string): void => {
  inFlight.delete(mapId);
  notify();
};

/** Whether a map's refresh is currently in flight. */
export const isRefreshInFlight = (mapId: string): boolean =>
  inFlight.has(mapId);

/**
 * Subscribe to in-flight changes (for `useSyncExternalStore`). Returns an
 * unsubscribe function.
 */
export const subscribeRefreshInFlight = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
